import React, { useState, useEffect, useCallback } from "react";
import { Box, Grid, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Stack, Select, MenuItem, FormControl, InputLabel, TablePagination, Tooltip, Zoom, IconButton, Card, CardContent } from '@mui/material';
import { CheckCircle, Launch, Warning, Visibility as VisibilityIcon, Assignment as TaskIcon, RadioButtonUnchecked as OpenIcon, CheckCircle as CompletedIcon, PendingActions as ProgressIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import { getStatusColor } from "../../../utils/statusColor";
import { formatDate } from "../../../utils/dateFormat";
import Loader from "../../../common/spinner";

const MyTaskList = () => {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('All');
    const [reviewFilter, setReviewFilter] = useState('All');
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;

    const [stats, setStats] = useState([
        { label: "Total Tasks", key: "totalTasks", count: 0, icon: <TaskIcon />, color: "#6366F1" },
        { label: "Todo", key: "todo", count: 0, icon: <OpenIcon />, color: "#F59E0B" },
        { label: "In Progress", key: "inProgress", count: 0, icon: < ProgressIcon />, color: "#3B82F6" },
        { label: "Completed", key: "completed", count: 0, icon: <CompletedIcon />, color: "#10B981" },
        { label: "Overdue", key: "overdue", count: 0, icon: <Warning />, color: "#EF4444" },
    ]);

    // Fixed: Wrapped in useCallback to prevent re-creation on every render
    const fetchTasks = useCallback(async () => {
        if (!userId) return;
        try {
            setLoading(true);

            // 1. Create the params object
            const queryParams = new URLSearchParams({
                page: page + 1,
                limit: limit
            });

            // 2. Add Status Filter if it's not "All"
            if (statusFilter !== 'All') {
                queryParams.append('status', statusFilter);
            }

            // 3. Add Review Filter if it's not "All"
            if (reviewFilter !== 'All') {
                queryParams.append('submit_for_review', reviewFilter === 'Yes');
            }

            // 4. Construct the URL correctly
            // API_ENDPOINTS.getEmployeeTasks(userId) returns something like "/api/.../getTasks?employeeId=10"
            // We append the rest of the filters using &
            const baseUrl = API_ENDPOINTS.getEmployeeTasks(userId);
            const finalUrl = `${baseUrl}&${queryParams.toString()}`;

            const response = await getData(finalUrl);

            if (response.status === 'SUCCESS') {
                const data = response.result;
                setTasks(data.tasks || []);
                setTotalCount(data.pagination?.totalItems || 0);

                const counts = data.status_counts;
                setStats(prev => prev.map(s => ({
                    ...s,
                    count: counts[s.key] || 0
                })));
            }
        } catch (error) {
          return null;
        } finally {
            setLoading(false);
        }
    }, [userId, page, limit, statusFilter, reviewFilter]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]); // Now fetchTasks is a stable dependency

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const StatCard = ({ title, value, icon, color }) => (
        <Card
            sx={{
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                height: '100%',
                background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
                color: '#FFFFFF',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600', opacity: 0.9 }}>
                            {title}
                        </Typography>
                        <Typography variant="h4" fontWeight="800" sx={{ mt: 0.5 }}>
                            {value}
                        </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', p: 1.5, borderRadius: '12px', display: 'flex', backdropFilter: 'blur(4px)' }}>
                        {React.cloneElement(icon, { sx: { color: '#FFFFFF', fontSize: 28 } })}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#1e293b' }}>
                    My Tasks
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="status-filter-label">Status</InputLabel>
                        <Select
                            labelId="status-filter-label"
                            id="status-filter"
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(0);
                            }}
                            sx={{ borderRadius: '8px', bgcolor: '#fff' }}
                        >
                            <MenuItem value="All">All</MenuItem>
                            <MenuItem value="Todo">Todo</MenuItem>
                            <MenuItem value="In Progress">In Progress</MenuItem>
                            <MenuItem value="Completed">Completed</MenuItem>
                            <MenuItem value="Re Open">Re-Open</MenuItem>
                            <MenuItem value="Hold">On Hold</MenuItem>
                            <MenuItem value="Overdue">Overdue</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="review-filter-label">Submitted for Review</InputLabel>
                        <Select
                            labelId="review-filter-label"
                            id="review-filter"
                            value={reviewFilter}
                            label="Submitted for Review"
                            onChange={(e) => {
                                setReviewFilter(e.target.value);
                                setPage(0);
                            }}
                            sx={{ borderRadius: '8px', bgcolor: '#fff' }}
                        >
                            <MenuItem value="All">All</MenuItem>
                            <MenuItem value="Yes">Yes</MenuItem>
                            <MenuItem value="No">No</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Box>

            <Grid container spacing={3} mb={4}>
                {stats.map((stat, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={index}>
                        <StatCard title={stat.label} value={stat.count} icon={stat.icon} color={stat.color} />
                    </Grid>
                ))}
            </Grid>

            <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#1E293B' }}>
                            <TableRow>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Task Name</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Project</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Due Date</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Est. Hours</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Actual Hours</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Priority</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Status</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Review</TableCell>
                                <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ bgcolor: '#fff' }}>
                            {loading ? (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 10 }}><Loader /></TableCell></TableRow>
                            ) : tasks.length > 0 ? (
                                tasks.map((task) => {
                                    const statusStyles = getStatusColor(task.status);
                                    return (
                                        <TableRow key={task.id} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                                            <TableCell sx={{ fontWeight: 600, color: '#334155', maxWidth: 300 }}>
                                                {task.task_title}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>{task.project_name || 'N/A'}</TableCell>
                                            <TableCell>{formatDate(task.due_date)}</TableCell>
                                            <TableCell sx={{ color: '#64748B' }}>{task.estimated_hours || 0} hrs</TableCell>
                                            <TableCell sx={{ color: '#64748B' }}>{task.actual_hours || 0} hrs</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={task.priority}
                                                    size="small"
                                                    sx={{
                                                        width: '90px',
                                                        bgcolor: task.priority === 'High' ? '#FEF2F2' : '#FFF7ED',
                                                        color: task.priority === 'High' ? '#EF4444' : '#F97316',
                                                        fontWeight: 600,
                                                        borderRadius: '12px',
                                                        px: 1
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label={task.status} size="small" sx={{ width: '160px', bgcolor: statusStyles.bg, color: statusStyles.color, fontWeight: 600, borderRadius: '24px', px: 1 }} />
                                            </TableCell>
                                            <TableCell align="center">
                                                {task.status === 'Completed' ? (
                                                    <Tooltip title="Task Completed" arrow TransitionComponent={Zoom}>
                                                        <CheckCircle sx={{ color: '#10B981', fontSize: 22, cursor: 'default' }} />
                                                    </Tooltip>
                                                ) : task.submit_for_review ? (
                                                    <Tooltip title="Submitted for Review" arrow TransitionComponent={Zoom}>
                                                        <Launch sx={{ color: '#FACC15', fontSize: 20, cursor: 'help' }} />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Not Yet Submitted" arrow TransitionComponent={Zoom}>
                                                        <Box component="span">
                                                            <CheckCircle sx={{ color: '#CBD5E1', fontSize: 20, opacity: 0.5, cursor: 'not-allowed' }} />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="View Details" arrow>
                                                    <IconButton size="small" sx={{ '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.08)' } }}
                                                        onClick={() => navigate(`/employee/my-tasks-details?taskId=${task.id}`)}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>No tasks found matching your filters.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalCount}
                rowsPerPage={limit}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    );
};

export default MyTaskList;